import { useEffect, useMemo, useState } from 'react'
import { useSearch } from '@tanstack/react-router'
import { Loader2, Printer } from 'lucide-react'

import { api } from '@/api/client'
import type { InvoiceData, InvoiceSetting } from '@/types/models'
import { BigSheet, SmallSheet } from './InvoiceSheets'

interface Bucket { partner: number; date: string; orderNo?: string }
type Layout = 'big' | 'small'

/** 여러 장의 거래명세서를 한 페이지에 렌더 + 자동 인쇄. 새 창으로 여는 뷰. */
export function InvoicePrintPage() {
  const qp = useSearch({ strict: false }) as { buckets?: string; autoprint?: string; layout?: string }
  const buckets = useMemo<Bucket[]>(() => {
    // buckets = "1|2026-07-01|STX...,2|2026-07-02|STX..." 형식 (order_no 선택적)
    if (!qp.buckets) return []
    return qp.buckets.split(',').map((token) => {
      const [pid, date, orderNo] = token.split('|')
      return { partner: Number(pid), date, orderNo: orderNo || undefined }
    }).filter((b) => b.partner && b.date)
  }, [qp.buckets])

  const [layout, setLayout] = useState<Layout>(qp.layout === 'small' ? 'small' : 'big')
  const [invoices, setInvoices] = useState<Array<{ data: InvoiceData; setting: InvoiceSetting }>>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string>('')

  useEffect(() => {
    if (!buckets.length) { setLoading(false); return }
    let cancelled = false
    ;(async () => {
      try {
        const results = await Promise.all(buckets.map(async (b) => {
          const orderParam = b.orderNo ? `&order=${encodeURIComponent(b.orderNo)}` : ''
          const [data, setting] = await Promise.all([
            api<InvoiceData>(`/api/invoice/preview?partner=${b.partner}&date=${b.date}${orderParam}`),
            api<InvoiceSetting>(`/api/invoice/setting?partner=${b.partner}`),
          ])
          return { data, setting }
        }))
        // 매출 외 출고유형(후납·교환 등) 줄뿐인 전표는 명세서가 없음 → 빈 장을 인쇄하지 않게 뺀다.
        if (!cancelled) { setInvoices(results.filter((r) => r.data.lines.length > 0)); setLoading(false) }
      } catch (e) {
        if (!cancelled) { setError(e instanceof Error ? e.message : String(e)); setLoading(false) }
      }
    })()
    return () => { cancelled = true }
  }, [buckets])

  // 인쇄 트리거 (?autoprint=1)
  useEffect(() => {
    if (loading || error || !invoices.length) return
    if (qp.autoprint === '1') {
      const t = setTimeout(() => window.print(), 300)
      return () => clearTimeout(t)
    }
  }, [loading, error, invoices, qp.autoprint])

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center text-slate-500 text-sm">
      <Loader2 className="w-5 h-5 animate-spin mr-2"/> 명세서 로딩 중...
    </div>
  )
  if (error) return (
    <div className="min-h-screen flex items-center justify-center text-rose-500 text-sm">
      로드 실패: {error}
    </div>
  )
  if (!invoices.length) return (
    <div className="min-h-screen flex items-center justify-center text-slate-500 text-sm">
      인쇄할 명세서가 없습니다. (후납·교환 등 매출 외 유형은 거래명세서에 나오지 않습니다)
    </div>
  )

  return (
    <div className="bg-slate-100 min-h-screen">
      {/* 큰장: A4 상하에 6mm 여백을 줘 프린터 비인쇄영역에서 글자가 잘리지 않게.
          (작은장은 index.css 의 margin:0 유지 — 297mm 를 정확히 반으로 절단하는 설계) */}
      {layout === 'big' && (
        <style>{`@media print { @page { size: A4; margin: 6mm 0; } }`}</style>
      )}
      <div className="no-print sticky top-0 bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3 shadow-sm z-10">
        <span className="text-sm text-slate-600">거래명세서 {invoices.length}장</span>
        <div className="inline-flex rounded-lg border border-slate-300 overflow-hidden text-sm">
          <button onClick={() => setLayout('big')}
                  className={layout === 'big' ? 'bg-blue-600 text-white px-3 py-1.5' : 'bg-white text-slate-600 px-3 py-1.5'}>큰장</button>
          <button onClick={() => setLayout('small')}
                  className={layout === 'small' ? 'bg-rose-600 text-white px-3 py-1.5' : 'bg-white text-slate-600 px-3 py-1.5'}>작은장</button>
        </div>
        <button onClick={() => window.print()}
                className="ml-auto bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-lg px-4 py-2 inline-flex items-center gap-1.5">
          <Printer className="w-4 h-4"/> 인쇄 / PDF로 저장
        </button>
      </div>

      <div className="py-4 space-y-6 print:space-y-0">
        {invoices.map((inv, idx) => (
          layout === 'big'
            ? <BigSheet key={idx} data={inv.data} setting={inv.setting} />
            : <SmallSheet key={idx} data={inv.data} setting={inv.setting} />
        ))}
      </div>
    </div>
  )
}
